import type { Theme } from './types';
import { THEME_ELIGIBLE_KEY, THEME_STORAGE_KEY } from './types';

/*
These keys are also read by the pre-paint script in frontend/index.html, which applies the
theme before React mounts. Keep the two in sync.
*/

// The browser chrome colour is set before any stylesheet loads, so it cannot read
// --color-bg-page; these mirror its value per theme.
export const THEME_COLOR_META: Record<Theme, string> = {
    light: '#f8f8fc',
    dark: '#0e1222',
};

export function isTheme(value: unknown): value is Theme {
    return value === 'light' || value === 'dark';
}

function readStorage(key: string): string | null {
    try {
        return localStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeStorage(key: string, value: string | null): void {
    try {
        if (value === null) {
            localStorage.removeItem(key);
        } else {
            localStorage.setItem(key, value);
        }
    } catch {
        // Private mode or blocked storage: the theme still applies for this page view.
    }
}

export function readStoredTheme(): Theme | null {
    const stored = readStorage(THEME_STORAGE_KEY);
    return isTheme(stored) ? stored : null;
}

export function writeStoredTheme(theme: Theme | null): void {
    writeStorage(THEME_STORAGE_KEY, theme);
}

export function readEligible(): boolean {
    return readStorage(THEME_ELIGIBLE_KEY) === 'true';
}

export function writeEligible(eligible: boolean): void {
    writeStorage(THEME_ELIGIBLE_KEY, eligible ? 'true' : null);
}

export function systemPrefersDark(): boolean {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(theme: Theme): void {
    const root = document.documentElement;
    if (root.dataset.theme !== theme) {
        root.dataset.theme = theme;
    }
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', THEME_COLOR_META[theme]);
}

/**
 * The theme a user should see: an explicit choice wins (profile first, then this browser),
 * otherwise the OS preference. While a choice is being saved, this browser's choice comes
 * first, since the profile only catches up once the save is done. Users without the flag
 * always get light.
 */
export function resolveTheme(options: {
    eligible: boolean;
    profileTheme: Theme | null | undefined;
    storedTheme: Theme | null;
    prefersDark: boolean;
    isSaving?: boolean;
}): Theme {
    const { eligible, profileTheme, storedTheme, prefersDark, isSaving = false } = options;
    if (!eligible) return 'light';
    if (isSaving && storedTheme) return storedTheme;
    if (profileTheme) return profileTheme;
    if (storedTheme) return storedTheme;
    return prefersDark ? 'dark' : 'light';
}
